import React, { useState, useEffect, useCallback } from 'react';
import { CustomButton } from '../../types';

/**
 * Simulated tap counter component for the landing page hero section.
 * This component mimics the real tap counter functionality but doesn't send data to the server.
 * It includes auto-incrementing to simulate other users tapping.
 */
function SimulatedTapCounter() {
  // Initialize with placeholder data inspired by the screenshot - reduced to 4 buttons
  const [buttons, setButtons] = useState<CustomButton[]>([
    {
      _id: 'sim-1',
      name: 'Silly Walrus',
      color: '#e91e63',
      icon: 'hand-thumbs-up',
      count: 6,
      createdAt: new Date().toISOString()
    },
    {
      _id: 'sim-2',
      name: 'Giggly Penguin',
      color: '#4caf50',
      icon: 'emoji-smile',
      count: 6,
      createdAt: new Date().toISOString()
    },
    {
      _id: 'sim-3',
      name: 'Wobbly Platypus',
      color: '#9c27b0',
      icon: 'gem',
      count: 13,
      createdAt: new Date().toISOString()
    },
    {
      _id: 'sim-4',
      name: 'Zippy Fox',
      color: '#ff9800',
      icon: 'hand-thumbs-up',
      count: 15,
      createdAt: new Date().toISOString()
    }
  ]);

  // Calculate total taps
  const totalTaps = buttons.reduce((sum, button) => sum + button.count, 0);

  // Handle button click - increment count locally
  const handleButtonClick = useCallback((buttonId: string) => {
    setButtons(prevButtons => 
      prevButtons.map(button => 
        button._id === buttonId 
          ? { ...button, count: button.count + 1 }
          : button
      )
    );
  }, []);

  // Simulate other users tapping by auto-incrementing random buttons
  useEffect(() => {
    const interval = setInterval(() => {
      setButtons(prevButtons => {
        const randomIndex = Math.floor(Math.random() * prevButtons.length);
        return prevButtons.map((button, index) => 
          index === randomIndex 
            ? { ...button, count: button.count + 1 }
            : button
        );
      });
    }, 2000 + Math.random() * 3000); // Random interval between 2-5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="simulated-counter">
      {/* Total Taps Display */}
      <div className="mb-4 py-4 px-3 bg-body-tertiary rounded">
        <div className="text-center">
          <h5 className="fs-6 text-muted mb-2">Total Taps</h5>
          <h2 className="display-6 fw-bold text-primary mb-0">{totalTaps}</h2>
        </div>
      </div>

      {/* Button Grid */}
      <div className="mb-4">
        <div className="row g-4">
          {buttons.map(button => (
            <div key={button._id} className="col-12 col-md-6">
              <button
                id={`button-${button._id}`}
                className="btn btn-lg w-100 dynamic-button"
                style={{ 
                  '--button-color-value': button.color || '#6c757d'
                } as React.CSSProperties}
                data-color
                onClick={() => handleButtonClick(button._id)}
              >
                <div className="d-flex align-items-center justify-content-center">
                  {button.icon && (
                    <i className={`bi bi-${button.icon} me-2 button-icon`} />
                  )}
                  <span>{button.name}</span>
                  <div className="d-flex align-items-center ms-2">
                    <div className="badge bg-light text-dark">
                      {button.count.toLocaleString()}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Demo Notice */}
      <div className="text-center">
        <small className="text-muted">
          <i className="bi bi-info-circle me-1"></i>
          <span className="d-none d-sm-inline">This is a live demo - taps are simulated and reset on page refresh</span>
          <span className="d-sm-none">Live demo - simulated taps</span>
        </small>
      </div>
    </div>
  );
}

export default SimulatedTapCounter;
